import React from "react";
import { Loader2, PhoneOff, Play, User } from "lucide-react";
import { PowerDialerCall, usePowerDialer } from "@/context/PowerDialerContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatTime } from "@/util/date";
import { getContactDisplayName, getInitials } from "@/util/string";

export function LiveCallCard({ call }: { call: PowerDialerCall }) {
  const { resumeCall, endCall, isCallActionPending } = usePowerDialer();

  const isHeld = call.status === "ON_HOLD";
  const displayName = getContactDisplayName(call.contact) ?? call.phoneNumber ?? "Unknown";
  const initials = getInitials(call.contact.firstName, call.contact.lastName);

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border p-3",
        isHeld ? "border-warning/40 bg-warning-soft/50" : "border-success/40 bg-success-soft/50",
      )}
    >
      <Avatar className="h-9 w-9">
        {call.contact.photo && <AvatarImage src={call.contact.photo} alt={displayName} />}
        <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
          {initials !== "?" ? initials : <User className="h-4 w-4" />}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-semibold dark:text-white">{displayName}</p>
        <p
          className={cn(
            "text-sm tabular-nums",
            isHeld ? "text-warning-soft-foreground" : "text-success-soft-foreground",
          )}
        >
          {isHeld ? "On hold" : "On call"} · {formatTime(call.duration)}
        </p>
      </div>
      {isHeld && (
        <Button
          size="sm"
          className="h-9 px-3.5 text-sm"
          onClick={() => resumeCall({ id: call.id })}
          disabled={isCallActionPending}
          title="Take this lead off hold"
        >
          {isCallActionPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
          Resume
        </Button>
      )}
      <Button
        size="icon"
        variant="ghost"
        className="h-9 w-9 text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={() => endCall({ id: call.id })}
        title="End this call"
      >
        <PhoneOff className="h-4 w-4" />
      </Button>
    </div>
  );
}
